<script setup lang="ts">
import { DsButton } from "@demvsystems/design-components";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/user/store/userStore";
import SvgVue from "@/application/components/SvgVue";
import { BiometryType } from "@/application/plugins/biometricAuth";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { ref, watch } from "vue";
import { isAndroid, isNativePlatform } from '@/application/utils/envInfo.ts';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { StatusBar, Style } from '@capacitor/status-bar';

const { isAppLocked, biometry } = storeToRefs(useUserStore());
const buttonIcon = ref('');

async function setSystemBarsColor(color: string): Promise<void> {
  await Promise.all([
    EdgeToEdge.setStatusBarColor({ color }),
    EdgeToEdge.setNavigationBarColor({ color }),
  ]);
}

watch(
  [biometry, isAppLocked],
  async ([newBiometry, appIsLocked]) => {
    if (!appIsLocked) {
      if (isAndroid) await setSystemBarsColor('#ffffff'); // white status bar if app is unlocked
      if (isNativePlatform) await StatusBar.setStyle({ style: Style.Light }); // dark text on light status bar
      return // early return to prevent calling unlockApp before the app is locked
    }

    if (isAndroid) await setSystemBarsColor('#2360B0'); // blue status bar if app is locked
    if(isNativePlatform) await StatusBar.setStyle({ style: Style.Dark }); // light text on dark status bar

    switch (newBiometry?.biometryType) {
      case BiometryType.touchId:
      case BiometryType.fingerprintAuthentication:
        buttonIcon.value = 'fingerprint';
        await unlock();
        break;
      case BiometryType.faceId:
      case BiometryType.faceAuthentication:
      case BiometryType.irisAuthentication:
        buttonIcon.value = 'face-viewfinder';
        break;
      default:
        buttonIcon.value = '';
    }
  },
  { immediate: true },
);

async function unlock(): Promise<void> {
  return await useUserStore().unlockApp();
}

async function handleUnlock() {
  useUserStore().resetAppUnlockErrorCode();
  await unlock();
}
</script>

<template>
  <Transition name="fade-out">
    <div
      v-if="isAppLocked"
      class="overlay h-dvh overflow-hidden grid w-full grid-rows-3 grid-cols-1 p-10 relative items-center"
      style="background: #2360B0;"
    >
      <div />
      <div>
        <SvgVue icon="logo.svg" alt="" class="w-32 mx-auto" />
      </div>
      <div class="pointer-events-auto">
        <div class="max-w-80 mx-auto ">
          <DsButton
            type="submit"
            variant="secondary"
            size="lg"
            class="w-full"
            :handler="handleUnlock"
          >
            <FontAwesomeIcon v-if="buttonIcon" :icon="['fal', buttonIcon]" class="mr-2" size="xl" />
            Entsperren
          </DsButton>
        </div>
      </div>
    </div>
  </Transition>
</template>
